import { z } from "zod";
import { AgentChannel } from "@common/constants/agent-channel";
import { SpreadsheetReferenceSchema } from "@common/schemas/spreadsheetReference.schema";
import { TransactionReferenceSchema } from "@common/schemas/transactionReference.schema";
import { CounterpartyReferenceSchema } from "@common/schemas/counterpartyReference.schema";
import { InvoiceReferenceSchema } from "@common/schemas/invoiceReference.schema";
import { DocumentReferenceSchema } from "@common/schemas/documentReference.schema";
import { MessageReactionSchema } from "@common/schemas/chatReaction.schema";

export enum MessageType {
    TEXT = "text",
    ONBOARDING_RECEIPT_MATCHING_OPENING = "onboardingReceiptMatchingOpening",
    ONBOARDING_INVITE_ACCOUNTANT_OPENING = "onboardingInviteAccountantOpening",
    SLATE = "slate",
    AUDIT_LOGS = "auditLogs",
    API = "api",
}

export const MessageSentBySchema = z.object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    iconColor: z.string(),
    avatar: z.string().nullable(),
    name: z.string(),
    email: z.string().nullable(),
});
export type MessageSentBy = z.infer<typeof MessageSentBySchema>;

export const MessageAttachmentSchema = z.object({
    /** Null when the underlying document has been deleted (tombstone). */
    financialDocumentId: z.number().int().nullable(),
    /** Filename snapshot taken when the attachment was sent. */
    fileName: z.string(),
    /** True when the underlying document no longer exists. */
    isDeleted: z.boolean(),
});
export type MessageAttachment = z.infer<typeof MessageAttachmentSchema>;

export const MessageSchema = z.object({
    id: z.string(),
    threadId: z.string(),
    userId: z.number().nullable(),
    content: z.string(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    user: MessageSentBySchema.optional().nullable(),
    type: z.nativeEnum(MessageType).nullable(),
    organizationId: z.string().uuid().nullable().optional(),
    // Subthread metadata (only populated on top-level messages)
    subthreadId: z.string().uuid().nullable().optional(),
    linkedSubthreadId: z.string().uuid().optional(),
    replyCount: z.number().int().optional(),
    lastReplyAt: z.coerce.date().nullable().optional(),
    /** Structured spreadsheet cell references attached to this message */
    spreadsheetReferences: z
        .array(SpreadsheetReferenceSchema)
        .nullable()
        .optional(),
    transactionReferences: z
        .array(TransactionReferenceSchema)
        .nullable()
        .optional(),
    /** Structured counterparty references attached to this message */
    counterpartyReferences: z
        .array(CounterpartyReferenceSchema)
        .nullable()
        .optional(),
    /** Structured invoice references attached to this message */
    invoiceReferences: z.array(InvoiceReferenceSchema).nullable().optional(),
    /** Structured financial document references attached to this message */
    documentReferences: z.array(DocumentReferenceSchema).nullable().optional(),
    isDeleted: z.boolean().optional(),
    /** Financial document attachments sent with this message */
    attachments: z.array(MessageAttachmentSchema).nullable().optional(),
    /** Timestamp when this message was resolved. Null if not resolved. */
    resolvedAt: z.coerce.date().nullable().optional(),
    /** ID of the user who resolved this message. Null if not resolved. */
    resolvedByUserId: z.number().int().nullable().optional(),
    /** Channel the message was posted on; `agent-run` marks a Kick agent comment */
    agentChannel: z.nativeEnum(AgentChannel).nullable().optional(),
    /** Emoji reactions on this message, in order of first reaction. Omitted when there are none. */
    reactions: z.array(MessageReactionSchema).optional(),
});
export type Message = z.infer<typeof MessageSchema>;

export const SubthreadSchema = z.object({
    id: z.string().uuid(),
    workspaceId: z.string().uuid(),
    threadId: z.string().uuid(),
    rootMessageId: z.string().uuid(),
    lastMessageAt: z.coerce.date().nullable().optional(),
    replyCount: z.number().int(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export type Subthread = z.infer<typeof SubthreadSchema>;

export const ThreadSchema = z.object({
    id: z.string(),
    workspaceId: z.string(),
    title: z.string(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    lastMessageAt: z.coerce.date().optional().nullable(),
    lastReadAt: z.coerce.date().optional().nullable(),
    taskId: z.string().optional(),
});
export type Thread = z.infer<typeof ThreadSchema>;

export const ThreadWithLastMessageSchema = ThreadSchema.extend({
    lastMessage: MessageSchema.optional(),
});
export type ThreadWithLastMessage = z.infer<typeof ThreadWithLastMessageSchema>;
